import type { GatewayBrowserClient } from "../gateway.ts";
import type {
  SessionEnvironmentExplainResult,
  SessionEnvironmentRecreateResult,
  SessionPreviewRegisterResult,
} from "../types.ts";
import { SESSION_KEYED_CACHE_MAX_SESSIONS, rememberSessionKeyedEntry } from "./session-uploads.ts";

export type SessionEnvironmentState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  sessionKey: string;
  sessionEnvironmentLoading: boolean;
  sessionEnvironmentError: string | null;
  sessionEnvironment: SessionEnvironmentExplainResult | null;
  sessionEnvironmentRecreating: boolean;
  sessionPreviewRegistering: boolean;
  /**
   * JAISIU-3307 (Pillar B) — write-through cache of the explain payload
   * per session key. Populated when a fetch resolves; consulted when a
   * session switch happens so the inspector paints the previous session's
   * environment back without spinning the network round-trip. Hosts that
   * don't care leave it `undefined`; the controller silently falls back
   * to the legacy behaviour.
   */
  sessionEnvironmentBySession?: Map<string, SessionEnvironmentExplainResult>;
};

function resolvePublicOrigin(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.location.origin;
}

function isUnknownSessionEnvironmentMethod(err: unknown): boolean {
  const msg = String(err);
  return (
    msg.includes("unknown method: session.environment.explain") ||
    msg.includes("unknown method: session.environment.recreate")
  );
}

export function formatSessionEnvironmentError(err: unknown): string {
  if (isUnknownSessionEnvironmentMethod(err)) {
    return "Session environment API needs a newer gateway. Rebuild (pnpm build) and restart the gateway.";
  }
  return String(err);
}

export async function loadSessionEnvironment(state: SessionEnvironmentState): Promise<void> {
  if (!state.client || !state.connected) {
    state.sessionEnvironment = null;
    state.sessionEnvironmentError = null;
    state.sessionEnvironmentLoading = false;
    return;
  }
  const sessionKey = state.sessionKey.trim();
  if (!sessionKey) {
    state.sessionEnvironment = null;
    return;
  }
  // JAISIU-3307 (Pillar B) — never null the live payload before the
  // network round-trip. The previous behaviour wrote `null` at the
  // very top, blanking the inspector for the whole duration of the
  // session.environment.explain RPC (typically 50–500 ms) and giving
  // the operator a "gone!" moment every time the inspector was
  // re-rendered while a probe was already in flight. The renderer
  // already renders "environment is loading" via the separate
  // sessionEnvironmentLoading flag — that's the one place to look for
  // staleness, the data field stays put.
  state.sessionEnvironmentLoading = true;
  state.sessionEnvironmentError = null;
  try {
    const next = await state.client.request<SessionEnvironmentExplainResult>(
      "session.environment.explain",
      { sessionKey, publicOrigin: resolvePublicOrigin() },
    );
    // JAISIU-3307 — gate the write by the session key still matching
    // what the caller asked for. A session switch between dispatch
    // and resolve must NOT paint chat-A's environment onto chat-B.
    if (state.sessionKey.trim() === sessionKey) {
      state.sessionEnvironment = next;
    }
    // Written in both branches: the key is the cache, not the active session.
    // Bounded, because this map had a comment claiming a cap that was never
    // implemented — every session visited left an entry.
    if (state.sessionEnvironmentBySession) {
      rememberSessionKeyedEntry(
        state.sessionEnvironmentBySession,
        sessionKey,
        next,
        SESSION_KEYED_CACHE_MAX_SESSIONS,
      );
    }
  } catch (err) {
    state.sessionEnvironmentError = formatSessionEnvironmentError(err);
  } finally {
    if (state.sessionKey.trim() === sessionKey) {
      state.sessionEnvironmentLoading = false;
    }
    // For a stale fetch (session already switched away) the next
    // activation will own `sessionEnvironmentLoading`.
  }
}

export async function recreateSessionEnvironment(
  state: SessionEnvironmentState,
): Promise<SessionEnvironmentRecreateResult | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const sessionKey = state.sessionKey.trim();
  if (!sessionKey) {
    return null;
  }
  state.sessionEnvironmentRecreating = true;
  state.sessionEnvironmentError = null;
  try {
    const result = await state.client.request<SessionEnvironmentRecreateResult>(
      "session.environment.recreate",
      { sessionKey, removeWorkspace: true },
    );
    await loadSessionEnvironment(state);
    return result;
  } catch (err) {
    state.sessionEnvironmentError = formatSessionEnvironmentError(err);
    return null;
  } finally {
    state.sessionEnvironmentRecreating = false;
  }
}

export async function registerSessionPreview(
  state: SessionEnvironmentState,
  containerPort: number,
): Promise<SessionPreviewRegisterResult | null> {
  if (!state.client || !state.connected) {
    return null;
  }
  const sessionKey = state.sessionKey.trim();
  if (!sessionKey) {
    return null;
  }
  state.sessionPreviewRegistering = true;
  state.sessionEnvironmentError = null;
  try {
    const result = await state.client.request<SessionPreviewRegisterResult>(
      "session.preview.register",
      {
        sessionKey,
        containerPort,
        publicOrigin: resolvePublicOrigin(),
      },
    );
    await loadSessionEnvironment(state);
    return result;
  } catch (err) {
    state.sessionEnvironmentError = formatSessionEnvironmentError(err);
    return null;
  } finally {
    state.sessionPreviewRegistering = false;
  }
}

export async function clearSessionPreview(state: SessionEnvironmentState): Promise<void> {
  if (!state.client || !state.connected) {
    return;
  }
  const sessionKey = state.sessionKey.trim();
  if (!sessionKey) {
    return;
  }
  state.sessionEnvironmentError = null;
  try {
    await state.client.request("session.preview.clear", { sessionKey });
    await loadSessionEnvironment(state);
  } catch (err) {
    state.sessionEnvironmentError = formatSessionEnvironmentError(err);
  }
}
